import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { submitPortability, fetchOperators, Operator, PortabilityResponse } from '../api/middleware';
import { useSettings } from '../Settings';
import { useTranslation, interpolate } from '../i18n';

interface PortabilityModalProps {
  open: boolean;
  onClose: () => void;
  middlewareUrl: string;
}

export function PortabilityModal({ open, onClose, middlewareUrl }: PortabilityModalProps) {
  const { settings } = useSettings();
  const { t } = useTranslation();
  const [numeroContel, setNumeroContel] = useState('');
  const [doadorNumero, setDoadorNumero] = useState('');
  const [doadorOperadora, setDoadorOperadora] = useState<number | ''>('');
  const [operators, setOperators] = useState<Operator[]>([]);
  const [operatorsLoading, setOperatorsLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PortabilityResponse | null>(null);

  useEffect(() => {
    if (!open || !middlewareUrl) return;
    setOperatorsLoading(true);
    fetchOperators(middlewareUrl)
      .then(setOperators)
      .catch(() => setOperators([]))
      .finally(() => setOperatorsLoading(false));
  }, [open, middlewareUrl]);

  function handleClose() {
    setNumeroContel('');
    setDoadorNumero('');
    setDoadorOperadora('');
    setError(null);
    setResult(null);
    setLoading(false);
    onClose();
  }

  function formatPhone(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }

  function getRaw(formatted: string): string {
    return formatted.replace(/\D/g, '');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const rawContel = getRaw(numeroContel);
    if (rawContel.length < 10 || rawContel.length > 11) {
      setError(t.portabilityModal.errorInvalidContel);
      return;
    }

    const rawDoador = getRaw(doadorNumero);
    if (rawDoador.length < 10 || rawDoador.length > 11) {
      setError(t.portabilityModal.errorInvalidDonor);
      return;
    }

    if (!doadorOperadora) {
      setError(t.portabilityModal.errorSelectOperator);
      return;
    }

    setLoading(true);
    try {
      const response = await submitPortability(middlewareUrl, {
        numero_contel: rawContel,
        doador_numero: rawDoador,
        doador_id_operadora: doadorOperadora as number,
      });
      setResult(response);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        t.portabilityModal.errorGeneric;
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: '16px' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'var(--color-primary, #6B00D6)',
          color: '#fff',
        }}
      >
        <Typography fontWeight={700} fontSize="1.1rem">
          {t.portabilityModal.title}
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: '#fff' }} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 1, mt: 0, overflow: 'visible' }}>
        {result ? (
          <Box textAlign="center" py={2}>
            <Typography variant="h6" fontWeight={700} color="success.main" mb={2}>
              {t.portabilityModal.successTitle}
            </Typography>
            {result.protocolo && (
              <Typography variant="body1" mb={1}>
                {t.portabilityModal.protocolLabel}: <strong>{result.protocolo}</strong>
              </Typography>
            )}
            {result.message && (
              <Typography variant="body2" color="text.secondary" mb={2}>
                {result.message}
              </Typography>
            )}
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit} id="portability-form">
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <TextField
              label={interpolate(t.portabilityModal.yourNumberLabel, {
                brand: settings.companyName || t.portabilityModal.yourNumberFallbackBrand,
              })}
              placeholder="(11) 99999-9999"
              value={numeroContel}
              onChange={(e) => setNumeroContel(formatPhone(e.target.value))}
              fullWidth
              required
              sx={{ mb: 2, mt: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">+55</InputAdornment>,
              }}
            />
            <TextField
              label={t.portabilityModal.donorNumberLabel}
              placeholder="(11) 99999-9999"
              value={doadorNumero}
              onChange={(e) => setDoadorNumero(formatPhone(e.target.value))}
              fullWidth
              required
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">+55</InputAdornment>,
              }}
            />
            <TextField
              select
              label={t.portabilityModal.donorOperatorLabel}
              value={doadorOperadora}
              onChange={(e) => setDoadorOperadora(Number(e.target.value))}
              fullWidth
              required
              sx={{ mb: 2 }}
              disabled={operatorsLoading || operators.length === 0}
              helperText={operatorsLoading ? t.portabilityModal.loadingOperators : undefined}
            >
              {operators.map((op) => (
                <MenuItem key={op.id} value={op.id}>
                  {op.descricao}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {result ? (
          <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: '8px', textTransform: 'none' }}>
            {t.common.close}
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
              {t.common.cancel}
            </Button>
            <Button
              type="submit"
              form="portability-form"
              variant="contained"
              disabled={loading || operatorsLoading}
              sx={{
                bgcolor: 'var(--color-primary, #6B00D6)',
                '&:hover': { bgcolor: 'var(--color-primary-dark, #4A0099)' },
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 600,
                minWidth: 160,
              }}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : t.portabilityModal.submitButton}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
